import { RegionModel, ILookupService, HttpMethod, ApiResultModel } from '@truckdown/systems';
import { UserContext } from '@truckdown/systems/Context/UserContext';
import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, onMounted, onUnmounted, computed } from 'vue';
import { AddressValidator } from './AddressValidator';
import { AddressModel } from '@truckdown/accounts';
import { IBillingService, EditBillingPreferenceModel } from '@truckdown/billing';
import validationError from '../../Validation/Error/Error.vue';

interface IData {
    isLoading: Ref<boolean>;
    errorMessage: Ref<string | undefined>;
    addressValidator: Ref<AddressValidator>;
    address: Ref<AddressModel>;
    regions: Ref<RegionModel[]>;
}

const getData = function () {
    let address = {
        line1: '',
        line2: '',
        city: '',
        regionCode: '',
        countryCode: '',
        postalCode: ''
    } as AddressModel;
    return {
        isLoading: ref<boolean>(false),
        errorMessage: ref<string | undefined>(),
        addressValidator: ref<AddressValidator>(new AddressValidator(address)),
        address: ref<AddressModel>(address),
        regions: ref<RegionModel[]>([])
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        selectableRegions: computed(() => {
            if (data.address.value.countryCode.length == 0) {
                return [];
            }
            return data.regions.value.filter(r => r.country == data.address.value.countryCode);
        })
    }
}

const getMethods = function (data: IData) {
    return {

    }
}

const loadRegions = async function (data: IData) {
    try {
        data.isLoading.value = true;

        const lookupService = await context.getService<ILookupService>('ILookupService');
        let regionLookupResult = await lookupService.getRegions();
        data.regions.value = regionLookupResult;
    }
    catch (err) {
        console.log('Error getting regions', err);
    }
    finally {
        data.isLoading.value = false;
    }
}

const loadAddress = async function (data: IData) {
    try {
        data.isLoading.value = true;
        const service = await context.getService<IBillingService>('IBillingService');
        const res = await service.getBillingSummary(context.user?.accountId ?? '');
        if (res) {
            if (res.address) {
                data.address.value = res.address;
                data.addressValidator.value = new AddressValidator(data.address.value);
            }
        } else {
            console.log('Error getting address');
        }
    }
    catch (err) {
        console.log('Error getting address', err);
    }
    finally {
        data.isLoading.value = false;
    }
}

const isAddressValid = function (data: IData): boolean {
    data.addressValidator.value = new AddressValidator(data.address.value)
    return data.addressValidator.value.validate();
};

const saveAddress = async function (data: IData) {
    try {
        const service = await context.getService<IBillingService>('IBillingService');

        let model = {
            address: {
                line1: data.address.value.line1,
                line2: data.address.value.line2 || "",
                city: data.address.value.city,
                regionCode: data.address.value.regionCode,
                countryCode: data.address.value.countryCode,
                postalCode: data.address.value.postalCode
            }
        } as EditBillingPreferenceModel;
        const result = await service.updatePreferences(model, context.user?.accountId ?? '');
        if (result) {
            return true;
        } else {
            return false;
        }
    }
    catch (err) {
        console.log('Error saving address', err);
        return false;
    }
}

const refreshProgress = async function (data: IData) {
    try {
        let manager = context.apiManager;
        let req = manager.createRequest({
            service: 'main',
            method: HttpMethod.GET,
            path: '/systems/context/refresh-client',
        });
        var res = await manager.get<any>(req);
        if (res) {
            let val = JSON.stringify(res);
            let newContext = new UserContext(context.antiforgeryToken, val);

            context.settings = newContext.settings;
            context.claims = newContext.claims;
            context.user = newContext.user;
            context.authToken = newContext.authToken;
            context.events.emit("refresh_onboarding_progress");
        } else {
            console.log('Error refreshing user context.');
        }
    }
    catch (err) {
        console.log('Error refreshing user context.');
    }
}

const activateFleetAccount = async function (data: IData) {
    try {
        let manager = context.apiManager;
        let request = manager.createRequest({
            service: "main",
            path: "/accounts/fleet-onboarding/setting/complete",
            method: HttpMethod.POST
        });
        var res = await manager.get<ApiResultModel>(request);
        if (res.ok) {
            await refreshProgress(data);
            return true;
        } else {
            console.log('Error activate account', res);
            return false;
        }
    }
    catch (err) {
        console.log('Error activate account', err);
        return false;
    }
}

const activateAccount = async function (data: IData) {
    if (!data.isLoading.value) {
        try {
            data.isLoading.value = true;
            if (isAddressValid(data)) {
                let addressRes = await saveAddress(data);
                if (addressRes) {
                    var res = await activateFleetAccount(data);
                    if (res) {
                        window.location.href = '/accounts/fleet/welcome';
                    } else {
                        data.errorMessage.value = 'Error activating your account.  Please contact us for support.';
                    }
                } else {
                    data.errorMessage.value = 'Error saving your address.';
                }
            }
        }
        catch (err) {
            console.log('Error activating account', err);
        }
        finally {
            data.isLoading.value = false;
        }
    }
    console.log('Completed step');
}

export default defineComponent({
    props: {
    },
    name: "fleet-setup-activate",
    emits: [],
    components: {
        "error": validationError
    },
    setup: function (props: any, ctx: any) {
        const data = getData();
        const computed = getComputed(data);
        const methods = getMethods(data);

        const handler = async function () {
            await activateAccount(data);
        }

        onMounted(async () => {
            await loadRegions(data);
            await loadAddress(data);

            context.events.on('fleet-setup-activate', handler);
        })

        onUnmounted(() => {
            context.events.off('fleet-setup-activate', handler);
        })

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});